/**
 * Bundle Loader Service
 * Orchestrates dynamic loading of role-based code bundles
 * Ensures protected bundles are only loaded after authentication
 */

import { tools } from './tools.js'

let storeInstance = null
async function loadStore() {
    if (!storeInstance) {
        storeInstance = (await import('./store.js')).store
    }
    return storeInstance
}

// Import bundle registry (async to avoid circular deps)
async function loadBundleRegistry() {
    try {
        const module = await import('../bundles/index.ts')
        return {
            BUNDLE_REGISTRY: module.BUNDLE_REGISTRY,
            getBundleForRoute: module.getBundleForRoute,
            getBundlePath: module.getBundlePath,
            canAccessBundle: module.canAccessBundle,
            getAccessibleBundles: module.getAccessibleBundles,
            getBundleDependencies: module.getBundleDependencies
        }
    } catch (error) {
        console.error('[BundleLoader] Failed to load bundle registry:', error)
        throw error
    }
}

class BundleLoaderService {
    constructor() {
        this.state = {
            loaded: new Set(['public']), // Public always preloaded
            loading: new Set(),
            failed: new Set()
        }
        this.loadPromises = new Map()
        this.metrics = new Map()
        this.registryReady = false
        this.registry = null
    }

    /**
     * Initialize bundle registry (call once on app startup)
     */
    async initialize() {
        if (this.registryReady) return
        
        try {
            this.registry = await loadBundleRegistry()
            this.registryReady = true
            console.log('[BundleLoader] Bundle registry initialized')
        } catch (error) {
            console.error('[BundleLoader] Failed to initialize bundle registry:', error)
            throw error
        }
    }

    /**
     * Ensure registry is loaded before operations
     */
    async ensureRegistry() {
        if (!this.registryReady) {
            await this.initialize()
        }
        return this.registry
    }

    /**
     * Check if user has permission to load a bundle
     */
    async hasPermission(bundleType) {
        const registry = await this.ensureRegistry()
        const config = registry.BUNDLE_REGISTRY[bundleType]
        
        if (!config) {
            console.warn('[BundleLoader] Unknown bundle type:', bundleType)
            return false
        }
        
        // Public bundle always accessible
        if (!config.requiredRoles) {
            return true
        }

        try {
            const userContext = await tools.getUserContext()
            return registry.canAccessBundle(bundleType, userContext.role)
        } catch (error) {
            console.error(`[BundleLoader] Permission check failed for ${bundleType}:`, error)
            return false
        }
    }

    /**
     * Get bundle dependencies
     */
    async getDependencies(bundleType) {
        const registry = await this.ensureRegistry()
        return registry.getBundleDependencies(bundleType)
    }

    /**
     * Load a single bundle dynamically
     */
    async loadBundle(bundleType) {
        // Already loaded
        if (this.state.loaded.has(bundleType)) {
            console.log(`[BundleLoader] Bundle ${bundleType} already loaded`)
            return
        }

        // Currently loading - return existing promise
        if (this.state.loading.has(bundleType)) {
            console.log(`[BundleLoader] Bundle ${bundleType} already loading, waiting...`)
            return this.loadPromises.get(bundleType)
        }

        // Check permission
        const hasPermission = await this.hasPermission(bundleType)
        if (!hasPermission) {
            const error = new Error(`Insufficient permissions to load ${bundleType} bundle`)
            this.state.failed.add(bundleType)
            console.error('[BundleLoader] Permission denied:', bundleType)
            throw error
        }

        // Mark as loading
        this.state.loading.add(bundleType)
        this.metrics.set(bundleType, { startTime: Date.now() })

        const loadPromise = (async () => {
            try {
                console.log(`[BundleLoader] Loading ${bundleType} bundle...`)
                
                // Dynamically import the entry point for the bundle
                // This triggers Vite's code-split chunk loading
                switch (bundleType) {
                    case 'public':
                        // Public bundle is always preloaded, but ensure it's available
                        await import('../pages/login.tag')
                        break
                    case 'user':
                        await import('../pages/home.tag')
                        break
                    case 'customer-admin':
                        await import('../pages/admin/users.tag')
                        break
                    case 'system-admin':
                        await import('../pages/admin/customers.tag')
                        break
                    default:
                        throw new Error(`Unknown bundle type: ${bundleType}`)
                }

                // Update metrics
                const metric = this.metrics.get(bundleType)
                metric.endTime = Date.now()
                metric.duration = metric.endTime - metric.startTime

                this.state.loaded.add(bundleType)
                this.state.loading.delete(bundleType)
                
                console.log(`[BundleLoader] ✅ Bundle ${bundleType} loaded in ${metric.duration}ms`)
            } catch (error) {
                this.state.failed.add(bundleType)
                this.state.loading.delete(bundleType)
                console.error(`[BundleLoader] ❌ Failed to load ${bundleType} bundle:`, error)
                throw error
            }
        })()

        this.loadPromises.set(bundleType, loadPromise)
        await loadPromise
    }

    /**
     * Ensure bundle is loaded for a given route
     */
    async ensureBundleForRoute(routeName) {
        const registry = await this.ensureRegistry()
        const requiredBundle = registry.getBundleForRoute(routeName)
        
        if (!requiredBundle) {
            console.warn(`[BundleLoader] No bundle mapping found for route: ${routeName}`)
            return
        }

        if (requiredBundle === 'public') {
            return // Public bundle always available
        }

        // Load dependencies first
        const dependencies = await this.getDependencies(requiredBundle)
        for (const dep of dependencies) {
            if (!this.state.loaded.has(dep)) {
                await this.loadBundle(dep)
            }
        }

        // Load the required bundle
        await this.loadBundle(requiredBundle)
    }

    /**
     * Preload bundles for user's role (non-blocking background load)
     */
    async preloadBundlesForRole() {
        try {
            const userContext = await tools.getUserContext()
            const registry = await this.ensureRegistry()
            
            const bundlesToPreload = registry.getAccessibleBundles(userContext.role)
            
            console.log(`[BundleLoader] Preloading bundles for role ${userContext.role}:`, bundlesToPreload)
            
            // Preload in background without blocking
            for (const bundle of bundlesToPreload) {
                if (!this.state.loaded.has(bundle) && !this.state.loading.has(bundle)) {
                    // Fire and forget - don't await
                    this.loadBundle(bundle).catch(error => {
                        console.warn(`[BundleLoader] Background preload failed for ${bundle}:`, error)
                    })
                }
            }
        } catch (error) {
            console.error('[BundleLoader] Preload initialization failed:', error)
        }
    }

    /**
     * Get current bundle state (for debugging)
     */
    getBundleState() {
        return {
            loaded: Array.from(this.state.loaded),
            loading: Array.from(this.state.loading),
            failed: Array.from(this.state.failed)
        }
    }

    /**
     * Get loading metrics (for performance monitoring)
     */
    getMetrics() {
        const metrics = {}
        for (const [bundle, data] of this.metrics) {
            metrics[bundle] = data
        }
        return metrics
    }

    /**
     * Clear failed state to allow retry
     */
    clearFailedState(bundleType) {
        this.state.failed.delete(bundleType)
        this.loadPromises.delete(bundleType)
        console.log(`[BundleLoader] Cleared failed state for ${bundleType}, ready for retry`)
    }

    /**
     * Get bundle status summary
     */
    getStatus() {
        return {
            totalBundles: 4,
            loaded: this.state.loaded.size,
            loading: this.state.loading.size,
            failed: this.state.failed.size,
            bundles: this.getBundleState(),
            metrics: this.getMetrics()
        }
    }
}

// Export singleton with lazy getter pattern
let bundleLoaderInstance = null

export function getBundleLoader() {
    if (!bundleLoaderInstance) {
        bundleLoaderInstance = new BundleLoaderService()
    }
    return bundleLoaderInstance
}

// Lazy getter for backward compatibility with proper this binding
export const bundleLoader = new Proxy({}, {
    get(target, prop) {
        const instance = getBundleLoader()
        const value = instance[prop]
        // Bind methods to preserve 'this' context
        if (typeof value === 'function') {
            return value.bind(instance)
        }
        return value
    }
})
